import { useCallback, useEffect, useRef, useState } from "react";
import type { MaterialListItem } from "@edu/shared";
import { Empty } from "../../lib/Empty.tsx";
import { listErrorText } from "../../lib/list-error.ts";
import { LoadingRows } from "../../lib/loading.tsx";
import { useSession } from "../../lib/session.tsx";
import { absText, dayText } from "../../lib/when.ts";
import { MaterialViewer } from "./MaterialViewer.tsx";
import {
  BROKEN_LINK_TEXT,
  EMPTY_TEXT,
  EMPTY_TITLE,
  FILE_GONE_TEXT,
  linkHostText,
  mySourceText,
  RECEIPT_DISCLOSURE,
} from "./my-material-text.ts";
import { listMyMaterials, markOpened } from "./my-materials-api.ts";
import { auxClickOpens } from "../../lib/open-gesture.ts";

/**
 * Ссылки и файлы, присланные ученику (`docs/material-receipt-design.md`, часть
 * А, шаг 3, разделы 7, 8, 15).
 *
 * ⚠️ **Строка о том, что открытие видно преподавателю, стоит всегда**, и над
 * списком, а не в сноске (раздел 15): отметка, не объявленная ребёнку,
 * называется иначе, чем квитанция.
 *
 * ⚠️ **Чего на экране нет, и это правило, а не недосмотр** (раздел 7): ни
 * названия группы, ни числа получателей, ни других детей. Сервер их ученику и не
 * отдаёт (`receiptsOf`), а экран не выводит даже собственной квитанции: ученику
 * она ничего не говорит, а строка «открыто 14 сентября» у себя читается как
 * контроль, а не как список.
 *
 * Отметка уходит по НАЖАТИЮ «Открыть», а не по показу строки: список увидеть
 * и материал открыть это разные вещи. У ссылки нажатие это и есть переход, у
 * файла это открытие просмотра.
 */
export function MyMaterialsPage() {
  const { state } = useSession();
  const me = state.status === "signed" ? state.me.userId : "";

  const [items, setItems] = useState<MaterialListItem[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [viewing, setViewing] = useState<MaterialListItem | null>(null);
  /** Поколение: догрузка, запрошенная до перечитывания, не приклеивается к новому списку. */
  const generation = useRef(0);

  const load = useCallback(
    (from: string | null) => {
      if (me === "") return;
      if (from === null) generation.current += 1;
      const mine = generation.current;
      setBusy(true);
      listMyMaterials(me, from)
        .then((page) => {
          if (generation.current !== mine) return;
          setItems((prev) => (from === null ? page.items : [...(prev ?? []), ...page.items]));
          setCursor(page.nextCursor);
          setErr(null);
        })
        .catch((e: unknown) => {
          if (generation.current === mine) {
            setErr(listErrorText(e, "Не вышло получить список. Попробуйте ещё раз."));
          }
        })
        .finally(() => {
          if (generation.current === mine) setBusy(false);
        });
    },
    [me],
  );

  useEffect(() => {
    load(null);
  }, [load]);

  const opened = (id: string) => {
    // Отказ отметки ученику не показываем: открыть материал он ему не мешает
    markOpened(id).catch(() => undefined);
  };

  const now = Date.now();

  return (
    <main className="home home--list">
      <header className="head">
        <div className="head__line">
          <h1 className="head__h">Ссылки и файлы</h1>
        </div>
      </header>

      <p className="hint">{RECEIPT_DISCLOSURE}</p>

      {err !== null && <p className="err">{err}</p>}

      {items === null ? (
        err === null ? (
          <LoadingRows />
        ) : (
          <p className="acts">
            <button type="button" className="btn" disabled={busy} onClick={() => load(null)}>
              Попробовать ещё раз
            </button>
          </p>
        )
      ) : items.length === 0 ? (
        <Empty icon="folder" title={EMPTY_TITLE} text={EMPTY_TEXT} />
      ) : (
        <section className="card">
          <ul className="list list--my-mat">
            <li className="list__head" aria-hidden="true">
              <span>Прислано</span>
              <span>Название</span>
              <span />
            </li>
            {items.map((m) => (
              <li key={m.id} className="row">
                <span className="cell row__when" data-label="Прислано" title={absText(m.createdAt)}>
                  {dayText(m.createdAt, now)}
                </span>
                <span className="cell row__name">
                  {m.title}
                  <span className="row__sub">{mySourceText(m)}</span>
                </span>
                <span className="cell row__act">
                  <MaterialAction item={m} onOpen={opened} onView={setViewing} />
                </span>
              </li>
            ))}
          </ul>

          {cursor !== null && (
            <p className="acts">
              <button type="button" className="btn" disabled={busy} onClick={() => load(cursor)}>
                {busy ? "Загрузка…" : "Показать ещё"}
              </button>
            </p>
          )}
        </section>
      )}

      {viewing !== null && <MaterialViewer item={viewing} onClose={() => setViewing(null)} />}
    </main>
  );
}

/**
 * Кнопка строки.
 *
 * ⚠️ **Ссылка это `<a href>` с `noopener noreferrer`, а не кнопка со скриптом**
 * (раздел 9.6 проекта): переход формой убил бы `form-action 'self'` молча, а
 * без `noreferrer` чужой сайт узнал бы наш адрес. Домен стоит открытым текстом
 * рядом (раздел 8): адрес ввёл взрослый, а нажимает ребёнок.
 *
 * Отметка уходит и по нажатию колёсиком (`onAuxClick`): вкладка, открытая в
 * фоне, это тоже открытие. Но ТОЛЬКО колёсиком: `auxclick` приходит и на
 * правую кнопку, а «копировать адрес» из меню открытием не является
 * (`open-gesture.ts`).
 */
function MaterialAction(props: {
  item: MaterialListItem;
  onOpen: (id: string) => void;
  onView: (item: MaterialListItem) => void;
}) {
  const m = props.item;

  if (m.kind === "link") {
    const host = linkHostText(m.url);
    if (host === null || m.url === null) return <span className="muted">{BROKEN_LINK_TEXT}</span>;
    const url = m.url;
    /*
      Пара «кнопка и домен» раскладкой ссылки на созвон (`.call`), но домен
      своим классом, а не `.call__host`: тот на узкой строке списка прячется
      (разбор в `styles.css`), а здесь домен обязан стоять на любой ширине
    */
    return (
      <span className="call">
        <a
          className="btn btn--sm"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => props.onOpen(m.id)}
          onAuxClick={(e) => {
            if (auxClickOpens(e.button)) props.onOpen(m.id);
          }}
        >
          Открыть
        </a>
        <span className="muted mymat__host">{host}</span>
      </span>
    );
  }

  if (m.fileId === null) return <span className="muted">{FILE_GONE_TEXT}</span>;

  return (
    <button
      type="button"
      className="btn btn--sm"
      onClick={() => {
        props.onOpen(m.id);
        props.onView(m);
      }}
    >
      Открыть
    </button>
  );
}
